import { Row, Col, Typography, Empty } from 'antd';
import { useQueries } from '@tanstack/react-query';
import { StatCard } from './StatCard';
import { apiClient } from '../api/client';
import type { DashboardWidget, ReportConfig } from '../types/panelConfig';

const { Title } = Typography;

const colorMap: Record<string, string> = {
  blue: '#1890ff',
  green: '#52c41a',
  purple: '#722ed1',
  orange: '#fa8c16',
  red: '#f5222d',
  cyan: '#13c2c2',
};

interface DynamicDashboardProps {
  widgets: DashboardWidget[];
  reports: ReportConfig[];
}

export function DynamicDashboard({ widgets, reports }: DynamicDashboardProps) {
  // Find sourceId for each widget's report
  const widgetQueries = widgets.map((widget) => {
    const report = reports.find((r) => r.code === widget.reportCode);
    return {
      widget,
      report,
      sourceId: report?.sourceId,
    };
  });

  // Fetch data for each widget that has a sourceId
  const queries = useQueries({
    queries: widgetQueries.map((wq) => ({
      queryKey: ['dashboard-widget', wq.widget.reportCode, wq.sourceId],
      queryFn: async () => {
        if (!wq.sourceId) return null;
        const token = localStorage.getItem('token');
        const { data } = await apiClient.post(
          '/api/v1/query',
          { sourceId: wq.sourceId, parameters: {} },
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        return data.data || [];
      },
      enabled: !!wq.sourceId,
      staleTime: 1000 * 60 * 5,
    })),
  });

  if (widgets.length === 0) {
    return <Empty description="Dashboard henüz yapılandırılmamış" />;
  }

  return (
    <div>
      <Title level={4}>Dashboard</Title>
      <Row gutter={[16, 16]}>
        {widgetQueries.map((wq, index) => {
          const query = queries[index];
          const { statField, label, aggregation, color } = wq.widget.config;

          let value: number | string = '-';
          if (query.data && Array.isArray(query.data) && query.data.length > 0) {
            const values = query.data.map(
              (row: Record<string, unknown>) => Number(row[statField]) || 0
            );
            switch (aggregation) {
              case 'sum':
                value = values.reduce((a: number, b: number) => a + b, 0);
                break;
              case 'avg':
                value = values.reduce((a: number, b: number) => a + b, 0) / values.length;
                break;
              case 'count':
                value = values.length;
                break;
              default:
                value = values.reduce((a: number, b: number) => a + b, 0);
            }
            // Format as currency
            value = new Intl.NumberFormat('tr-TR', {
              style: 'currency',
              currency: 'TRY',
            }).format(value as number);
          }

          return (
            <Col xs={24} sm={12} lg={6} key={wq.widget.reportCode}>
              <StatCard
                title={label}
                value={value}
                color={colorMap[color || 'blue'] || color}
                loading={query.isLoading}
              />
            </Col>
          );
        })}
      </Row>
    </div>
  );
}
